import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAutomationKey } from 'app/models/Orders/OrderPreference';
import { serviceActions } from './async-actions';
import { initialState } from './initialState';
import { IBookingTemplate, ExistingUnitPreferences, IFacilityPreferences, IFacilityTemplate, ITemplate, IFacilityTargetMargin } from './types';
import _ from 'lodash';
import { validateFacilityTargetMarginRows, isDirtyRows } from './utils';

const slice = createSlice({
  name: 'facilityPreferences',
  initialState,
  reducers: {
    setPreferences(state, action: PayloadAction<IFacilityPreferences>) {
      return { ...state, ...action.payload };
    },
    setActivePlacementCounts(state, action: PayloadAction<{ rfo: number; sent: number; others: number }>) {
      return {
        ...state,
        submissionLimits: { activePlacementCounts: { ...action.payload } }
      };
    },
    setUpdateAutomationApiProgressStatus(state, action: PayloadAction<{ status: boolean | false }>) {
      return {
        ...state,
        updateInprogress: action.payload.status,
      };
    },
    setPreferencesByStage(state, action: PayloadAction<{ key: IAutomationKey; data }>) {
      return {
        ...state,
        [action.payload.key]: {
          ...action.payload.data,
        },
      };
    },
    setConfirmationTemplates(state, action: PayloadAction<ITemplate[]>) {
      return { ...state, confirmationTemplates: [...action.payload] };
    },
    setFacilityTemplate(state, action: PayloadAction<IFacilityTemplate>) {
      return { ...state, facilityTemplate: action.payload };
    },
    setFacilityBookingTemplates(state, action: PayloadAction<IBookingTemplate>) {
      return { ...state, bookingTemplate: action.payload };
    },
    setIBookingFacilityDirty(state, action: PayloadAction<boolean>) {
      return { ...state, bookingFacilityDirty: action.payload };
    },
    setBookingFacilityReset(state, action: PayloadAction<boolean>) {
      return { ...state, bookingFacilityReset: action.payload };
    },
    setConfirmationDialog(state, action: PayloadAction<boolean>) {
      state.confirmationDialog = action.payload
    },
    setTabValue(state, action: PayloadAction<number>) {
      state.tabValue = action.payload
    },
    setIsPreferenceChaged(state, action: PayloadAction<boolean>) {
      state.isPreferenceChanged = action.payload
    },
    setUpdateFacilityTemplateProgressStatus(state, action: PayloadAction<{ status: boolean | false }>) {
      return {
        ...state,
        templateUpdateInProgress: action.payload.status,
      };
    },
    setMiscellaneousData(
      state,
      action: PayloadAction<{ key: 'disableEmailContainer' | 'disablePacketContainer'; value: boolean }>,
    ) {
      return { ...state, miscellaneous: { ...state.miscellaneous, [action.payload.key]: action.payload.value } };
    },
    setExsistingUnits(state, action: PayloadAction<ExistingUnitPreferences[]>) {
      return { ...state, existingUnits: action.payload };
    },
    setPreferenceUpdateDialog(state, action: PayloadAction<boolean>) {
      return { ...state, updateDialogOpen: action.payload };
    },
    setPreferenceSelection(state, action: PayloadAction<number[]>) {
      return { ...state, preferenceSelection: action.payload };
    },
    //START -  FACILITY TARGET MARGIN
    removeFacilityTargetMarginsById(state, action: PayloadAction<string[]>) {
      const updatedMargins = state.facilityTargetMargins.filter(m => !action.payload.includes(m.clientId));
      const errorValidatedTargetMargin = validateFacilityTargetMarginRows(updatedMargins);
      const dirty = isDirtyRows(errorValidatedTargetMargin, state.originalFacilityTargetMargins);
      return { ...state, facilityTargetMargins: errorValidatedTargetMargin, isDirty: dirty };
    },
    setFacilityTargetMargins(state, action: PayloadAction<IFacilityTargetMargin[]>) {
      const dirty = isDirtyRows(action.payload, state.originalFacilityTargetMargins);
      return { ...state, facilityTargetMargins: action.payload, isDirty: dirty };
    },
     updateFacilityTargetMargin(state, action: PayloadAction<IFacilityTargetMargin>) {
      const updatedMargin = action.payload;
      const targetMargin = _.cloneDeep(state.facilityTargetMargins);
      const marginIndex = state.facilityTargetMargins.findIndex(
        margin => margin.clientId === updatedMargin.clientId
      );
      if (marginIndex !== -1) {
        targetMargin[marginIndex] = updatedMargin;
      }else{
        targetMargin.push(updatedMargin);
      }
      const errorValidatedTargetMargin = validateFacilityTargetMarginRows(targetMargin);
      const dirty = isDirtyRows(errorValidatedTargetMargin, state.originalFacilityTargetMargins);
      return { ...state, facilityTargetMargins: errorValidatedTargetMargin, isDirty: dirty };
    },
    setSkillsetLookupData(state, action: PayloadAction<any>) {
      return { ...state, skillsetLookupData: action.payload };
    },
    setFacilityTargetMarginsBaseline(state, action: PayloadAction<IFacilityTargetMargin[]>) {
      return { ...state, originalFacilityTargetMargins: _.cloneDeep(action.payload), isDirty: false };
    },
    //END -  FACILITY TARGET MARGIN
  },
});

export const facilityPreferenceActions = {
  ...slice.actions,
  ...serviceActions,
};

export const { name: facilityPreferenceSliceKey, reducer: facilityPreferenceReducer } = slice;
